import * as L from 'leaflet';
import 'leaflet-polylinedecorator';

import { lineString, point } from '@turf/helpers';
import nearestPointOnLine from '@turf/nearest-point-on-line';
import distance from '@turf/distance';

const startBtn = document.getElementById('startBtn');
const completeBtn = document.getElementById('completeBtn');
const resetBtn = document.getElementById('resetBtn');
const timerDisplay = document.getElementById('timer');
const timeLimitDisplay = document.getElementById('timeLimit');
const colorIndicator = document.getElementById('colorIndicator');
const gpxStats = document.getElementById('gpxStats');
const totalDistEl = document.getElementById('totalDist');
const estDistEl = document.getElementById('estDist');
const loopButtons = document.querySelectorAll('.loop-btn');
const paceButtons = document.querySelectorAll('.pace-btn');
const limitDisplay = document.getElementById('limitDisplay');
const progressBar = document.getElementById('progressBar');
const reqMphEl = document.getElementById('reqMph');
const gpsSpeedEl = document.getElementById('gpsSpeed');
const historyContainer = document.getElementById('historyContainer');
const manualAddBtns = document.querySelectorAll('.manual-add-btn');
const totalRaceDistEl = document.getElementById('totalRaceDist');
const remDistEl = document.getElementById('remDist');
const distMethodEl = document.getElementById('distMethod');
const confirmModal = document.getElementById('confirmModal');
const confirmMsg = document.getElementById('confirmMsg');
const confirmYes = document.getElementById('confirmYes');
const confirmNo = document.getElementById('confirmNo');
const timesUpTag = document.getElementById('timesUpTag');

let pendingAction = null;

function showConfirm(msg, action) {
    confirmMsg.textContent = msg;
    pendingAction = action;
    confirmModal.classList.remove('hidden');
}

function hideConfirm() {
    confirmModal.classList.add('hidden');
    pendingAction = null;
}

let timerInterval;
let watchId;
let courseTotalMiles = 0;
let selectedLoop = 'Green';
let selectedPaceSeconds = 870;
let totalGpsDist = 0;
let lastCoords = null;
let completedLoops = [];
let cachedGpxData = JSON.parse(localStorage.getItem('lrs_gpxData') || '{}');
let hasGpsFix = false;

let map;
let trackLayer;
let trackDecoratorLayer;
let userMarker;
let userCircle;

const loopDistances = {
    'Green': 2.42,
    'Orange': 4.4,
    'Blue': 4.53,
    'Red': 7.19,
    'Purple': 7.3
};

confirmYes.addEventListener('click', () => {
    if (pendingAction) pendingAction();
    hideConfirm();
});
confirmNo.addEventListener('click', hideConfirm);

confirmModal.addEventListener('click', (e) => {
    if (e.target === confirmModal) hideConfirm();
});

loopButtons.forEach(btn => {
    btn.addEventListener('click', () => {
        selectLoop(btn.dataset.color);
    });
});

paceButtons.forEach(btn => {
    btn.addEventListener('click', () => {
        selectPace(parseInt(btn.dataset.pace));
    });
});

function selectLoop(color, skipConfirm = false) {
    // Quality improvement: ignore if the loop is already selected
    if (!skipConfirm && color === selectedLoop && localStorage.getItem('lrs_color')) return;

    if (!skipConfirm && localStorage.getItem('lrs_startTime')) {
        showConfirm("The timer is running. Do you want to switch the loop distance?", () => {
            selectLoop(color, true);
        });
        return;
    }

    selectedLoop = color;
    localStorage.setItem('lrs_color', color);
    
    loopButtons.forEach(b => {
        const isActive = b.dataset.color === color;
        b.classList.toggle('active', isActive);
    });
    colorIndicator.style.backgroundColor = color.toLowerCase();
    progressBar.style.backgroundColor = color.toLowerCase();
    
    // If custom GPX load fails, fallback to hardcoded distance
    if (!courseTotalMiles) courseTotalMiles = loopDistances[color];

    updateTotalTimeDisplay();
    loadLoopGpx(color);
}

function selectPace(pace, skipConfirm = false) {
    // Quality improvement: ignore if the pace is already selected
    if (!skipConfirm && pace === selectedPaceSeconds && localStorage.getItem('lrs_pace')) return;

    if (!skipConfirm && localStorage.getItem('lrs_startTime')) {
        showConfirm("The timer is running. Do you want to switch the allowed pace?", () => {
            selectPace(pace, true);
        });
        return;
    }

    selectedPaceSeconds = pace;
    localStorage.setItem('lrs_pace', pace);

    paceButtons.forEach(b => {
        const isActive = parseInt(b.dataset.pace) === pace;
        b.classList.toggle('active', isActive);
    });

    updateTotalTimeDisplay();
}

function updateRequiredMph() {
    if (!courseTotalMiles || !selectedPaceSeconds) {
        reqMphEl.textContent = "0.0";
        return;
    }

    const maxSeconds = Math.floor(selectedPaceSeconds * courseTotalMiles);
    const bufferSeconds = 300; // 5 minute buffer
    const targetTimeSeconds = maxSeconds - bufferSeconds;

    if (targetTimeSeconds > 0) {
        const reqMph = courseTotalMiles / (targetTimeSeconds / 3600);
        reqMphEl.textContent = reqMph.toFixed(2);
    } else {
        reqMphEl.textContent = "N/A";
    }
}

function updateTotalTimeDisplay() {
    const pace = selectedPaceSeconds;
    const totalSeconds = Math.floor(pace * courseTotalMiles);
    const paceStr = formatTime(pace);
    const distStr = courseTotalMiles.toFixed(2);
    limitDisplay.textContent = `Dist: ${distStr}mi | Pace: ${paceStr}`;
    if (timeLimitDisplay) {
        timeLimitDisplay.textContent = formatTime(totalSeconds);
    }
    updateRequiredMph();
}

function initMap() {
    map = L.map('map').setView([35.0, -85.0], 13);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors'
    }).addTo(map);

    // Add custom buttons for recentering track and locating user
    const MapControls = L.Control.extend({
        options: { position: 'topleft' },
        onAdd: function (map) {
            const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
            L.DomEvent.disableClickPropagation(container);
            
            const recenterBtn = L.DomUtil.create('a', '', container);
            recenterBtn.innerHTML = '🎯';
            recenterBtn.title = 'Recenter on Track';
            recenterBtn.href = '#';
            recenterBtn.style.fontSize = '18px';
            recenterBtn.style.lineHeight = '30px';
            recenterBtn.onclick = (e) => {
                e.preventDefault();
                if (trackLayer) map.fitBounds(trackLayer.getBounds());
            };

            const locateBtn = L.DomUtil.create('a', '', container);
            locateBtn.innerHTML = '📍';
            locateBtn.title = 'Locate Me';
            locateBtn.href = '#';
            locateBtn.style.fontSize = '18px';
            locateBtn.style.lineHeight = '30px';
            locateBtn.onclick = (e) => {
                e.preventDefault();
                const latlng = userMarker.getLatLng();
                if (latlng.lat !== 0 || latlng.lng !== 0) {
                    map.setView(latlng, 17);
                }
            };
            return container;
        }
    });
    map.addControl(new MapControls());

    // Ensure the map updates its layout if the container is moved or resized
    const mapContainer = document.getElementById('map');
    if (mapContainer) new ResizeObserver(() => map.invalidateSize()).observe(mapContainer);
    window.addEventListener('resize', () => map.invalidateSize());
    
    // Show the user as a rainbow gradient dot twice as wide as the track line (10px)
    userMarker = L.marker([0,0], {
        icon: L.divIcon({
            className: 'user-dot-icon',
            html: '<div style="width:10px;height:10px;border-radius:50%;background:conic-gradient(red, yellow, lime, aqua, blue, magenta, red);border:1px solid white;"></div>',
            iconSize: [10, 10],
            iconAnchor: [5, 5]
        })
    }).addTo(map);
}

async function fetchAndParseGpx(color) {
    const url = `https://endure.nad27.net/${color}_Loop.gpx`;
    const response = await fetch(url);
    if (!response.ok) throw new Error('GPX not found');
    const gpxText = await response.text();
    const xmlDoc = new DOMParser().parseFromString(gpxText, "text/xml");
    const trackPoints = xmlDoc.getElementsByTagName('trkpt');
    
    const latlngs = [];
    const turfCoords = [];
    const distancesAlongTrack = [];
    let totalDistance = 0;

    for (let i = 0; i < trackPoints.length; i++) {
        const lat = parseFloat(trackPoints[i].getAttribute('lat'));
        const lon = parseFloat(trackPoints[i].getAttribute('lon'));
        latlngs.push([lat, lon]);
        turfCoords.push([lon, lat]);
        distancesAlongTrack.push(totalDistance);
        
        if (i > 0) {
            totalDistance += calculateDistance(
                parseFloat(trackPoints[i-1].getAttribute('lat')), parseFloat(trackPoints[i-1].getAttribute('lon')),
                lat, lon
            );
        }
    }
    
    const trailFeature = lineString(turfCoords);
    return { latlngs, distance: totalDistance, distancesAlongTrack, trailFeature };
}

async function pullAllGpx() {
    const colors = Object.keys(loopDistances);
    let changed = false;
    for (const color of colors) {
        if (!cachedGpxData[color] || !cachedGpxData[color].distancesAlongTrack) {
            try {
                const data = await fetchAndParseGpx(color);
                cachedGpxData[color] = data;
                changed = true;
            } catch (e) {
                console.error(`Failed to preload GPX for ${color}:`, e);
            }
        }
    }
    if (changed) {
        localStorage.setItem('lrs_gpxData', JSON.stringify(cachedGpxData));
    }
}

async function loadLoopGpx(color) {
    if (cachedGpxData[color]) {
        displayGpx(cachedGpxData[color]);
        return;
    }

    try {
        const data = await fetchAndParseGpx(color);
        cachedGpxData[color] = data;
        localStorage.setItem('lrs_gpxData', JSON.stringify(cachedGpxData));
        displayGpx(data);
        
        // If one was missing, ensure we pull everything else into cache now
        pullAllGpx();

    } catch (error) {
        console.error("Error loading GPX:", error);
        // Fallback to static distance if fetch fails
        courseTotalMiles = loopDistances[color];
        totalDistEl.textContent = courseTotalMiles.toFixed(2);
    }
}

function displayGpx(data) {
    if (trackLayer) map.removeLayer(trackLayer);
    if (trackDecoratorLayer) map.removeLayer(trackDecoratorLayer);
    
    const trackColor = selectedLoop.toLowerCase();
    trackLayer = L.polyline(data.latlngs, { color: trackColor, weight: 5, opacity: 0.25 }).addTo(map);
    map.fitBounds(trackLayer.getBounds());

    const patterns = [];
    const numMiles = Math.floor(data.distance);

    // Add arrows and labels exactly at every full mile
    for (let i = 1; i <= numMiles; i++) {
        const offsetPercent = (i / data.distance) * 100 + '%';
        
        patterns.push({
            offset: offsetPercent,
            symbol: L.Symbol.arrowHead({
                pixelSize: 18,
                headAngle: 29,
                polygon: true,
                pathOptions: { fillOpacity: 1, weight: 1, color: 'black', fillColor: trackColor, className: 'track-arrow' }
            })
        });

        patterns.push({
            offset: offsetPercent,
            symbol: L.Symbol.marker({
                rotate: false, // Keep text upright
                markerOptions: {
                    icon: L.divIcon({
                        className: 'mile-label-icon',
                        html: `<div class="mile-label">${i} mi</div>`,
                        iconSize: [40, 20],
                        iconAnchor: [20, 30] // Shift the text slightly above the arrow so it doesn't overlap
                    })
                }
            })
        });
    }

    trackDecoratorLayer = L.polylineDecorator(trackLayer, { patterns }).addTo(map);

    courseTotalMiles = data.distance;
    totalDistEl.textContent = data.distance.toFixed(2);

    // Ensure distancesAlongTrack exists (patch for older cached data)
    if (!data.distancesAlongTrack) {
        data.distancesAlongTrack = [];
        let total = 0;
        for (let i = 0; i < data.latlngs.length; i++) {
            if (i > 0) total += calculateDistance(data.latlngs[i-1][0], data.latlngs[i-1][1], data.latlngs[i][0], data.latlngs[i][1]);
            data.distancesAlongTrack.push(total);
        }
        localStorage.setItem('lrs_gpxData', JSON.stringify(cachedGpxData));
    }

    // Ensure trailFeature exists (patch for older cached data)
    if (!data.trailFeature && data.latlngs && data.latlngs.length > 0) {
        const turfCoords = data.latlngs.map(ll => [ll[1], ll[0]]); // Leaflet is [lat, lng], Turf is [lng, lat]
        data.trailFeature = lineString(turfCoords);
        localStorage.setItem('lrs_gpxData', JSON.stringify(cachedGpxData));
    }

    updateTotalTimeDisplay();
}

/**
 * Updates all calculation and timer-related UI elements.
 * Centralizing this ensures consistency across the app.
 */
function updateDisplay(elapsedSeconds) {
    const maxSeconds = Math.floor(selectedPaceSeconds * courseTotalMiles);
    
    timerDisplay.textContent = formatTime(elapsedSeconds);

    // Update Progress Bar
    const progress = Math.min((elapsedSeconds / maxSeconds) * 100, 100);
    progressBar.style.width = `${progress}%`;

    if (elapsedSeconds >= maxSeconds) {
        timerDisplay.style.color = "var(--accent)";
        progressBar.style.backgroundColor = "var(--accent)";
        timesUpTag.classList.remove('hidden');
    } else {
        timerDisplay.style.color = "var(--text-color)";
        progressBar.style.backgroundColor = selectedLoop.toLowerCase();
        timesUpTag.classList.add('hidden');
    }

    updateRequiredMph();
}

function initState() {
    initMap();
    pullAllGpx();

    const savedColor = localStorage.getItem('lrs_color');
    const savedPace = localStorage.getItem('lrs_pace');
    const savedStartTime = localStorage.getItem('lrs_startTime');
    const savedGpsDist = localStorage.getItem('lrs_gpsDist');
    const savedHistory = localStorage.getItem('lrs_history');

    if (savedHistory) {
        completedLoops = JSON.parse(savedHistory);
    }
    
    selectLoop(savedColor || 'Green', true);
    selectPace(savedPace ? parseInt(savedPace) : 870, true);
    
    renderHistory();

    manualAddBtns.forEach(btn => {
        btn.addEventListener('click', () => {
            const color = btn.dataset.color;
            completedLoops.push({
                color: color,
                dist: loopDistances[color]
            });
            localStorage.setItem('lrs_history', JSON.stringify(completedLoops));
            renderHistory();
        });
    });

    if (savedGpsDist) totalGpsDist = parseFloat(savedGpsDist);

    if (savedStartTime) {
        const startTime = parseInt(savedStartTime);
        updateDisplay(Math.floor((Date.now() - startTime) / 1000));
        startCountdown(startTime);
        disableInputs(true);
    } else {
        updateTotalTimeDisplay();
        disableInputs(false);
    }
}

function renderHistory() {
    historyContainer.innerHTML = '';
    let total = 0;
    completedLoops.forEach((loop, index) => {
        const rect = document.createElement('div');
        rect.className = 'loop-rect';
        rect.style.backgroundColor = loop.color.toLowerCase();
        rect.textContent = loop.dist.toFixed(1);
        rect.title = `Click to remove ${loop.color} loop (${loop.dist}mi)`;
        
        rect.addEventListener('click', () => {
            showConfirm(`Remove this ${loop.color} loop?`, () => {
                completedLoops.splice(index, 1);
                localStorage.setItem('lrs_history', JSON.stringify(completedLoops));
                renderHistory();
            });
        });

        historyContainer.appendChild(rect);
        total += loop.dist;
    });
    totalRaceDistEl.textContent = total.toFixed(2);
}

function formatTime(seconds) {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;

    if (h > 0) {
        return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    }
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

function startCountdown(startTime) {
    clearInterval(timerInterval);
    startGpsTracking();

    timerInterval = setInterval(() => {
        updateDisplay(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
}

function disableInputs(active) {
    startBtn.disabled = active;
    completeBtn.disabled = !active;
    resetBtn.disabled = !active;
}

function startGpsTracking() {
    if (!navigator.geolocation) return;

    watchId = navigator.geolocation.watchPosition((position) => {
        const { latitude, longitude, speed, heading } = position.coords;
        const latlng = [latitude, longitude];

        hasGpsFix = true;
        if (distMethodEl) distMethodEl.textContent = "Based on GPS position pinned to track";
        updateTrackProgress(latitude, longitude);

        // Update Map Marker
        userMarker.setLatLng(latlng);
        
        if (heading !== null && heading !== undefined) {
            const iconDiv = userMarker.getElement().querySelector('div');
            if (iconDiv) {
                iconDiv.style.transform = `rotate(${heading}deg)`;
            }
        }

        // Calculate real-time speed (MPH)
        if (speed && speed > 0.5) { // Only update if moving faster than a crawl
            const mph = speed * 2.23694;
            gpsSpeedEl.textContent = mph.toFixed(1);
        } else {
            gpsSpeedEl.textContent = "0.0";
        }

        // Accumulate distance
        if (lastCoords) {
            const d = calculateDistance(lastCoords.lat, lastCoords.lon, latitude, longitude);
            // Filter out GPS drift (jumps > 0.1 miles in a second are likely errors)
            if (d < 0.1) {
                totalGpsDist += d;
                localStorage.setItem('lrs_gpsDist', totalGpsDist);
            }
        }
        lastCoords = { lat: latitude, lon: longitude };
    }, (err) => {
        console.error("Geolocation error:", err);
    }, {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
    });
}

startBtn.addEventListener('click', () => {
    const startTime = Date.now();
    localStorage.setItem('lrs_startTime', startTime);
    disableInputs(true);
    startCountdown(startTime);
});

completeBtn.addEventListener('click', () => {
    if (!localStorage.getItem('lrs_startTime')) return;
    
    showConfirm(`Are you sure you want to complete the ${selectedLoop} loop?`, () => {
        completedLoops.push({
            color: selectedLoop,
            dist: courseTotalMiles
        });
        localStorage.setItem('lrs_history', JSON.stringify(completedLoops));
        
        finishLoop();
        renderHistory();
    });
});

resetBtn.addEventListener('click', () => {
    showConfirm("Are you sure? This will end the current loop. History is preserved.", () => {
        finishLoop();
    });
});

function finishLoop() {
    clearInterval(timerInterval);
    if (watchId) navigator.geolocation.clearWatch(watchId);
    
    localStorage.removeItem('lrs_startTime');
    localStorage.removeItem('lrs_gpsDist');
    
    hasGpsFix = false;
    if (distMethodEl) distMethodEl.textContent = "Waiting for GPS track pinning...";

    timerDisplay.textContent = "00:00";
    timerDisplay.style.color = "var(--text-color)";
    timesUpTag.classList.add('hidden');
    progressBar.style.width = "0%";
    gpsSpeedEl.textContent = "0.0";
    reqMphEl.textContent = "0.0";
    totalGpsDist = 0;
    lastCoords = null;
    disableInputs(false);
    estDistEl.textContent = "0.00";
    if (remDistEl) remDistEl.textContent = "0.00";
}

function updateTrackProgress(lat, lon) {
    const data = cachedGpxData[selectedLoop];
    if (!data || !data.trailFeature) return;

    const userPoint = point([lon, lat]);
    const snapped = nearestPointOnLine(data.trailFeature, userPoint, {units: 'miles'});
    
    let distCovered = snapped.properties.location;
    
    // Loop Disambiguation (The "Start vs. Finish" Problem)
    const startTime = localStorage.getItem('lrs_startTime');
    if (startTime) {
        const elapsedSeconds = (Date.now() - parseInt(startTime)) / 1000;
        const expectedMph = 3600 / selectedPaceSeconds;
        const expectedDistance = (elapsedSeconds / 3600) * expectedMph;
        
        // If Turf snaps to the start (< 10%) but expected distance indicates they should be near the end (> 50%)
        if (distCovered < data.distance * 0.1 && expectedDistance > data.distance * 0.5) {
            const coords = data.trailFeature.geometry.coordinates;
            const lastCoord = coords[coords.length - 1];
            const distToEnd = distance(userPoint, point(lastCoord), {units: 'miles'});
            distCovered = Math.max(0, data.distance - distToEnd);
            if (distCovered > data.distance) distCovered = data.distance;
        }
    }

    // Off-Trail Deviation
    const offTrailDist = distance(userPoint, snapped, {units: 'miles'});
    if (offTrailDist > 0.05) {
        console.warn(`User is off-trail by ${offTrailDist.toFixed(3)} miles`);
    }

    // Move or create the snapped marker
    if (!userCircle) {
        userCircle = L.circleMarker([snapped.geometry.coordinates[1], snapped.geometry.coordinates[0]], {
            radius: 5,
            color: 'cyan',
            fillOpacity: 0.8
        }).addTo(map);
    } else {
        userCircle.setLatLng([snapped.geometry.coordinates[1], snapped.geometry.coordinates[0]]);
    }

    estDistEl.textContent = distCovered.toFixed(2);
    if (remDistEl) remDistEl.textContent = Math.max(0, data.distance - distCovered).toFixed(2);
}

function calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 3958.8;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
    return R * c; 
}

if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register(new URL('service-worker.js', import.meta.url), { type: 'module' })
        .catch(err => console.error('Service Worker registration failed:', err));
}

initState();